/**
 * Parse GameChanger season stats CSV and map to app sport_stats.
 * CSV has Batting, Pitching, and Fielding sections in one file; we split by section
 * and map only columns that have an app stat_key in our schemas.
 */

import { GAMECHANGER_BATTING_SCHEMA } from '../assets/schemas/gamechanger-batting-schema';
import { GAMECHANGER_PITCHING_SCHEMA } from '../assets/schemas/gamechanger-pitching-schema';
import { GAMECHANGER_FIELDING_SCHEMA } from '../assets/schemas/gamechanger-fielding-schema';

export interface ParsedPlayerStats {
  number: string;
  last: string;
  first: string;
  batting: Array<{ stat_key: string; value: number }>;
  pitching: Array<{ stat_key: string; value: number }>;
}

const SECTION_LABELS = ['Batting', 'Pitching', 'Fielding'] as const;

/**
 * Parse a single CSV line respecting quoted fields.
 */
function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '"') {
      i += 1;
      let field = '';
      while (i < line.length && line[i] !== '"') {
        if (line[i] === '\\') {
          i += 1;
          if (i < line.length) field += line[i];
        } else {
          field += line[i];
        }
        i += 1;
      }
      if (line[i] === '"') i += 1;
      result.push(field);
      if (line[i] === ',') i += 1;
    } else {
      let field = '';
      while (i < line.length && line[i] !== ',') {
        field += line[i];
        i += 1;
      }
      result.push(field.trim());
      if (line[i] === ',') i += 1;
    }
  }
  return result;
}

/**
 * Find column index where the section label appears in the section row (row 0).
 */
function findSectionStart(sectionRow: string[], label: string): number {
  const idx = sectionRow.findIndex((cell) => cell.trim() === label);
  return idx >= 0 ? idx : sectionRow.length;
}

/**
 * Parse GameChanger CSV text into rows and section boundaries.
 * Row 0 = section labels (Batting, Pitching, Fielding)
 * Row 1 = column headers
 * Row 2+ = data (skip empty and Glossary row)
 */
export function parseGameChangerCsv(csvText: string): {
  headers: string[];
  sectionStarts: { batting: number; pitching: number; fielding: number };
  dataRows: string[][];
} {
  const lines = csvText.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length < 2) {
    throw new Error('CSV must have at least a section row and a header row');
  }

  const sectionRow = parseCsvLine(lines[0]);
  const headerRow = parseCsvLine(lines[1]);

  const battingStart = findSectionStart(sectionRow, 'Batting');
  const pitchingStart = findSectionStart(sectionRow, 'Pitching');
  const fieldingStart = findSectionStart(sectionRow, 'Fielding');

  const dataRows: string[][] = [];
  for (let r = 2; r < lines.length; r++) {
    const row = parseCsvLine(lines[r]);
    const first = (row[0] ?? '').trim();
    if (first === 'Glossary' || first === '') continue;
    dataRows.push(row);
  }

  return {
    headers: headerRow,
    sectionStarts: { batting: battingStart, pitching: pitchingStart, fielding: fieldingStart },
    dataRows,
  };
}

/**
 * Map a row slice (array of values) to app stats using the given schema.
 * Schema columns are matched by header name; we use headerRow slice to get names.
 */
function mapRowToStats(
  headerSlice: string[],
  valueSlice: string[],
  schema: Array<{ gcHeader: string; appStatKey: string | null }>
): Array<{ stat_key: string; value: number }> {
  const result: Array<{ stat_key: string; value: number }> = [];
  const keyByHeader = new Map(schema.map((c) => [c.gcHeader, c.appStatKey]));
  for (let i = 0; i < headerSlice.length && i < valueSlice.length; i++) {
    const header = (headerSlice[i] ?? '').trim();
    const appKey = keyByHeader.get(header);
    if (!appKey) continue;
    const raw = (valueSlice[i] ?? '').trim();
    if (raw === '') continue;
    const num = parseFloat(raw);
    if (!Number.isFinite(num)) continue;
    result.push({ stat_key: appKey, value: num });
  }
  return result;
}

const REQUIRED_HEADERS = ['Number', 'Last', 'First'] as const;

/**
 * Parse full GameChanger CSV and return one ParsedPlayerStats per data row.
 * @throws Error if the file format is invalid (e.g. wrong header columns).
 */
export function parseGameChangerCsvToPlayerStats(csvText: string): ParsedPlayerStats[] {
  const { headers, sectionStarts, dataRows } = parseGameChangerCsv(csvText);
  const header0 = (headers[0] ?? '').trim();
  const header1 = (headers[1] ?? '').trim();
  const header2 = (headers[2] ?? '').trim();
  if (header0 !== REQUIRED_HEADERS[0] || header1 !== REQUIRED_HEADERS[1] || header2 !== REQUIRED_HEADERS[2]) {
    throw new Error(
      'Invalid file format: expected header columns "Number", "Last", "First". This file may be from a different export or the format is incorrect.'
    );
  }

  const { batting: batStart, pitching: pitchStart, fielding: fieldStart } = sectionStarts;

  const batEnd = pitchStart;
  const pitchEnd = fieldStart;

  const battingHeaders = headers.slice(batStart, batEnd);
  const pitchingHeaders = headers.slice(pitchStart, pitchEnd);

  const out: ParsedPlayerStats[] = [];
  for (const row of dataRows) {
    const number = (row[0] ?? '').trim();
    const last = (row[1] ?? '').trim();
    const first = (row[2] ?? '').trim();
    const battingValues = row.slice(batStart, batEnd);
    const pitchingValues = row.slice(pitchStart, pitchEnd);

    const batting = mapRowToStats(battingHeaders, battingValues, GAMECHANGER_BATTING_SCHEMA);
    const pitching = mapRowToStats(pitchingHeaders, pitchingValues, GAMECHANGER_PITCHING_SCHEMA);

    out.push({
      number,
      last,
      first,
      batting: batting.filter((s) => s.stat_key.length > 0),
      pitching: pitching.filter((s) => s.stat_key.length > 0),
    });
  }
  return out;
}